(() => {
  if (window.orrInfiniteScrollTrigger) return;

  const MIN_PRELOAD_DISTANCE = 800;
  const TRAILING_DELAY = 120;
  const ACTIVE_ATTRIBUTE = 'data-orr-product-list-infinite-scroll-active';
  let activeTriggerCount = 0;

  function updateScrollAnchorProtection() {
    const active = activeTriggerCount > 0;
    document.documentElement.toggleAttribute(ACTIVE_ATTRIBUTE, active);
  }

  function create(options) {
    const sentinel = options?.sentinel;
    if (!(sentinel instanceof HTMLElement)) {
      return {
        refresh() {},
        check() {
          return Promise.resolve(false);
        },
        destroy() {},
      };
    }

    let observer = null;
    let destroyed = false;
    let enabled = false;
    let loading = false;
    let listenersAttached = false;
    let frameId = 0;
    let trailingTimer = 0;
    let resizeTimer = 0;
    let lastScrollY = window.scrollY;
    let lastMovementWasDown = true;
    let scrollAnchorProtectionActive = false;

    const setScrollAnchorProtection = (active) => {
      if (scrollAnchorProtectionActive === active) return;
      scrollAnchorProtectionActive = active;
      activeTriggerCount = Math.max(0, activeTriggerCount + (active ? 1 : -1));
      updateScrollAnchorProtection();
    };

    const isEnabled = () => options.isEnabled?.() !== false;
    const canLoad = () => options.canLoad?.() === true;
    const preloadDistance = () => Math.max(window.innerHeight, MIN_PRELOAD_DISTANCE);

    const isInLoadRange = () => {
      if (!sentinel.isConnected) return false;
      return sentinel.getBoundingClientRect().top <= window.innerHeight + preloadDistance();
    };

    const check = async () => {
      if (destroyed || !enabled || loading || !canLoad() || !isInLoadRange()) return false;

      loading = true;
      let status = 'error';

      try {
        status = await options.load();
      } catch (error) {
        console.error('Error loading the next product-list page:', error);
      } finally {
        loading = false;
      }

      if (status === 'loaded') {
        scheduleCheck();
      } else if (status === 'done') {
        observer?.disconnect();
        observer = null;
        enabled = false;
        setScrollAnchorProtection(false);
        detachListeners();
        clearTimeout(trailingTimer);
        clearTimeout(resizeTimer);
      }

      return status === 'loaded';
    };

    const scheduleCheck = () => {
      if (destroyed || frameId) return;
      frameId = requestAnimationFrame(() => {
        frameId = 0;
        check();
      });
    };

    const handleScroll = () => {
      const nextScrollY = window.scrollY;
      lastMovementWasDown = nextScrollY >= lastScrollY;
      lastScrollY = nextScrollY;

      if (lastMovementWasDown) {
        scheduleCheck();
      }

      clearTimeout(trailingTimer);
      trailingTimer = setTimeout(() => {
        if (lastMovementWasDown) scheduleCheck();
      }, TRAILING_DELAY);
    };

    const handleScrollEnd = () => {
      if (lastMovementWasDown) scheduleCheck();
    };

    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(refresh, 200);
    };

    const attachListeners = () => {
      if (listenersAttached) return;
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', handleResize);
      if ('onscrollend' in window) {
        window.addEventListener('scrollend', handleScrollEnd);
      }
      listenersAttached = true;
    };

    const detachListeners = () => {
      if (!listenersAttached) return;
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if ('onscrollend' in window) {
        window.removeEventListener('scrollend', handleScrollEnd);
      }
      listenersAttached = false;
    };

    function refresh() {
      if (destroyed) return;

      observer?.disconnect();
      observer = null;
      enabled = isEnabled() && sentinel.isConnected;
      lastScrollY = window.scrollY;
      lastMovementWasDown = true;

      if (!enabled) {
        setScrollAnchorProtection(false);
        detachListeners();
        return;
      }

      setScrollAnchorProtection(true);
      attachListeners();

      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              const passedAboveViewport =
                !entry.isIntersecting &&
                entry.rootBounds &&
                entry.boundingClientRect.top < entry.rootBounds.top;

              if (entry.isIntersecting || passedAboveViewport) {
                scheduleCheck();
              }
            }
          },
          {
            rootMargin: `0px 0px ${preloadDistance()}px 0px`,
            threshold: 0,
          }
        );
        observer.observe(sentinel);
      }

      scheduleCheck();
    }

    const destroy = () => {
      if (destroyed) return;
      destroyed = true;
      observer?.disconnect();
      observer = null;
      setScrollAnchorProtection(false);
      detachListeners();
      cancelAnimationFrame(frameId);
      clearTimeout(trailingTimer);
      clearTimeout(resizeTimer);
      frameId = 0;
    };

    refresh();

    return {
      refresh,
      check,
      destroy,
    };
  }

  window.orrInfiniteScrollTrigger = { create };
})();
